/**
 * How a drawer's name is decided.
 *
 * `MANUAL` keeps whatever was typed. The other two are *derived* from the
 * drawer's position among its siblings, which is why the mode is stored rather
 * than just the resulting string: adding, deleting or reordering a drawer
 * changes what its siblings should be called, so the names have to be
 * recomputed for the whole cabinet on every such change.
 */
export type DrawerNameMode = 'MANUAL' | 'POSITION' | 'NUMBER';

export const DRAWER_NAME_MODES: readonly DrawerNameMode[] = ['MANUAL', 'POSITION', 'NUMBER'];

/** The noun the UI already falls back to for an unnamed drawer. */
const DRAWER_NOUN = 'Sertar';

/**
 * The name a drawer should carry, or `null` when the user names it themselves.
 *
 * `index` is 0-based and `total` is the number of drawers in the cabinet, both
 * in `orderIndex` order — top of the cabinet first.
 *
 * Position naming deliberately reuses "Mijloc" for every drawer between the
 * first and the last. Sus/Mijloc/Jos is how a fitter describes a drawer stack,
 * and inventing "Mijloc 2" to keep names unique would trade the vocabulary
 * people actually use for a uniqueness nobody asked for. Callers that need to
 * tell two drawers apart have `orderIndex`.
 */
export function drawerGeneratedName(
  mode: DrawerNameMode,
  index: number,
  total: number,
): string | null {
  if (mode === 'MANUAL') return null;
  if (mode === 'NUMBER') return `${DRAWER_NOUN} ${index + 1}`;
  // POSITION. A lone drawer is "Sus": it is the top one, and there is no
  // meaningful middle or bottom to contrast it with.
  if (index <= 0) return 'Sus';
  if (index >= total - 1) return 'Jos';
  return 'Mijloc';
}

/**
 * Names for a whole cabinet's drawers, in `orderIndex` order. `MANUAL` entries
 * come back as `null` so the caller leaves the stored name alone.
 */
export function drawerGeneratedNames(modes: readonly DrawerNameMode[]): Array<string | null> {
  return modes.map((mode, index) => drawerGeneratedName(mode, index, modes.length));
}
